import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { TodoWithMeta, TodoCategory, TodoPriority } from '../../types/todo.js';
import * as categoryApi from '../../services/category-api.js';
import * as todoApi from '../../services/todo-api.js';
import { TodoStateController } from '../../services/todo-state.js';
import { ASSIGNEE_LABELS } from '../board/board-constants.js';
import '../agents/cp-agent-selector.js';
import '../shared/cp-skill-selector.js';

const PRIORITY_OPTIONS: { key: TodoPriority; label: string }[] = [
  { key: 'low', label: 'Low' },
  { key: 'medium', label: 'Medium' },
  { key: 'high', label: 'High' },
  { key: 'critical', label: 'Critical' },
];

@customElement('cp-todo-form')
export class CpTodoForm extends LitElement {
  createRenderRoot() { return this; }

  private _todoState = new TodoStateController(this);

  @state() private _open = false;
  @state() private _editTodo: TodoWithMeta | null = null;
  @state() private _pseudoProjectId = 0;

  // Form fields
  @state() private _titleValue = '';
  @state() private _descriptionValue = '';
  @state() private _priorityValue: TodoPriority = 'medium';
  @state() private _categoryId: number | null = null;
  @state() private _deadlineValue = '';
  @state() private _assigneeValue: string | null = null;
  @state() private _agentId: number | null = null;
  @state() private _skillIds: string[] = [];
  @state() private _linkedProjectId: number | null = null;

  // UI states
  @state() private _submitting = false;
  @state() private _submitError = '';
  @state() private _titleError = '';
  @state() private _categories: TodoCategory[] = [];

  get isEditMode(): boolean {
    return this._editTodo !== null;
  }

  openCreate(pseudoProjectId: number): void {
    this._editTodo = null;
    this._pseudoProjectId = pseudoProjectId;
    this._resetForm();
    this._open = true;
    void this._loadCategories();
    this.updateComplete.then(() => {
      const input = this.querySelector<HTMLInputElement>('.cp-todo-form__input');
      input?.focus();
    });
  }

  openEdit(todo: TodoWithMeta): void {
    this._editTodo = todo;
    this._pseudoProjectId = todo.pseudo_project_id;
    this._titleValue = todo.title;
    this._descriptionValue = todo.description ?? '';
    this._priorityValue = todo.priority;
    this._categoryId = todo.category_id;
    this._deadlineValue = todo.due_date ? todo.due_date.split('T')[0] : '';
    this._assigneeValue = todo.assignee ?? null;
    this._agentId = todo.agent_id ?? null;
    this._skillIds = todo.skill_ids ?? [];
    this._linkedProjectId = todo.linked_project_id ?? null;
    this._submitting = false;
    this._submitError = '';
    this._titleError = '';
    this._open = true;
    void this._loadCategories();
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  private _close(): void {
    this._open = false;
    this._editTodo = null;
  }

  private _resetForm(): void {
    this._titleValue = '';
    this._descriptionValue = '';
    this._priorityValue = 'medium';
    this._categoryId = null;
    this._deadlineValue = '';
    this._assigneeValue = null;
    this._agentId = null;
    this._skillIds = [];
    this._linkedProjectId = null;
    this._submitting = false;
    this._submitError = '';
    this._titleError = '';
  }

  private async _loadCategories(): Promise<void> {
    try {
      this._categories = await categoryApi.getCategories();
    } catch {
      this._categories = [];
    }
  }

  private _onTitleInput(e: Event): void {
    this._titleValue = (e.target as HTMLInputElement).value;
    this._titleError = '';
    this._submitError = '';
  }

  private _onDescriptionInput(e: Event): void {
    this._descriptionValue = (e.target as HTMLTextAreaElement).value;
  }

  private _onPriorityChange(e: Event): void {
    this._priorityValue = (e.target as HTMLSelectElement).value as TodoPriority;
  }

  private _onCategoryChange(e: Event): void {
    const val = (e.target as HTMLSelectElement).value;
    this._categoryId = val ? Number(val) : null;
  }

  private _onDeadlineChange(e: Event): void {
    this._deadlineValue = (e.target as HTMLInputElement).value;
  }

  private _onAssigneeChange(e: Event): void {
    const val = (e.target as HTMLSelectElement).value;
    this._assigneeValue = val || null;
  }

  private _onAgentSelected(e: CustomEvent<{ agentId: number }>): void {
    this._agentId = e.detail.agentId;
    this._skillIds = []; // mutual exclusivity
  }

  private _onSkillsChanged(e: CustomEvent<{ skillIds: string[] }>): void {
    this._skillIds = e.detail.skillIds;
    if (this._skillIds.length > 0) this._agentId = null; // mutual exclusivity
  }

  private _onBackdropClick(e: Event): void {
    if ((e.target as HTMLElement).classList.contains('cp-todo-form__overlay')) {
      this._close();
    }
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._open) {
      this._close();
    }
  };

  private async _onSubmit(): Promise<void> {
    const title = this._titleValue.trim();

    if (!title) {
      this._titleError = 'Titel ist erforderlich';
      return;
    }

    if (this._submitting) return;

    this._submitting = true;
    this._submitError = '';

    try {
      if (this.isEditMode) {
        await todoApi.updateTodo(this._editTodo!.id, {
          title,
          description: this._descriptionValue.trim() || undefined,
          priority: this._priorityValue,
          category_id: this._categoryId,
          due_date: this._deadlineValue || null,
          assignee: this._assigneeValue,
          agent_id: this._agentId,
          skill_ids: this._skillIds.length > 0 ? this._skillIds : undefined,
        });
      } else {
        await todoApi.createTodo({
          title,
          description: this._descriptionValue.trim() || undefined,
          priority: this._priorityValue,
          pseudo_project_id: this._pseudoProjectId,
          category_id: this._categoryId ?? undefined,
          due_date: this._deadlineValue || undefined,
          assignee: this._assigneeValue ?? undefined,
          agent_id: this._agentId ?? undefined,
          skill_ids: this._skillIds.length > 0 ? this._skillIds : undefined,
        });
      }

      await this._todoState.refresh();
      this._close();
    } catch (e) {
      this._submitError = e instanceof Error ? e.message : 'To-Do konnte nicht gespeichert werden';
    } finally {
      this._submitting = false;
    }
  }

  private async _onDelete(): Promise<void> {
    if (!this._editTodo) return;

    const confirmed = window.confirm('To-Do wirklich loeschen?');
    if (!confirmed) return;

    this._submitting = true;
    this._submitError = '';

    try {
      await todoApi.deleteTodo(this._editTodo.id);
      await this._todoState.refresh();
      this._close();
    } catch (e) {
      this._submitError = e instanceof Error ? e.message : 'To-Do konnte nicht geloescht werden';
    } finally {
      this._submitting = false;
    }
  }

  render() {
    if (!this._open) return nothing;

    const canSubmit = this._titleValue.trim().length > 0 && !this._submitting;
    const dialogTitle = this.isEditMode ? 'To-Do bearbeiten' : 'Neues To-Do';
    const submitLabel = this.isEditMode
      ? (this._submitting ? 'Wird gespeichert...' : 'Speichern')
      : (this._submitting ? 'Wird erstellt...' : 'Erstellen');

    return html`
      <div class="cp-todo-form__overlay" @click=${this._onBackdropClick}>
        <div class="cp-todo-form__dialog">
          <div class="cp-todo-form__header">
            <h2 class="cp-todo-form__title">${dialogTitle}</h2>
            <button class="cp-todo-form__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-todo-form__body">
            <!-- Title -->
            <div class="cp-todo-form__field">
              <label class="cp-todo-form__label">Titel</label>
              <input
                class="cp-todo-form__input ${this._titleError ? 'cp-todo-form__input--error' : ''}"
                type="text"
                placeholder="Was muss erledigt werden?"
                .value=${this._titleValue}
                @input=${this._onTitleInput}
              />
              ${this._titleError ? html`
                <p class="cp-todo-form__validation-msg">${this._titleError}</p>
              ` : nothing}
            </div>

            <!-- Description -->
            <div class="cp-todo-form__field">
              <label class="cp-todo-form__label">Beschreibung</label>
              <textarea
                class="cp-todo-form__textarea"
                placeholder="Optionale Details..."
                .value=${this._descriptionValue}
                @input=${this._onDescriptionInput}
              ></textarea>
            </div>

            <!-- Priority + Category + Assignee row -->
            <div class="cp-todo-form__row">
              <div class="cp-todo-form__field">
                <label class="cp-todo-form__label">Prioritaet</label>
                <select
                  class="cp-todo-form__select"
                  .value=${this._priorityValue}
                  @change=${this._onPriorityChange}
                >
                  ${PRIORITY_OPTIONS.map(opt => html`
                    <option value=${opt.key} ?selected=${opt.key === this._priorityValue}>${opt.label}</option>
                  `)}
                </select>
              </div>

              <div class="cp-todo-form__field">
                <label class="cp-todo-form__label">Kategorie</label>
                <select
                  class="cp-todo-form__select"
                  @change=${this._onCategoryChange}
                >
                  <option value="" ?selected=${this._categoryId === null}>Keine Kategorie</option>
                  ${this._categories.map(cat => html`
                    <option value=${cat.id} ?selected=${this._categoryId === cat.id}>${cat.name}</option>
                  `)}
                </select>
              </div>

              <div class="cp-todo-form__field">
                <label class="cp-todo-form__label">Assignee</label>
                <select
                  class="cp-todo-form__select"
                  @change=${this._onAssigneeChange}
                >
                  <option value="" ?selected=${this._assigneeValue === null}>Kein Assignee</option>
                  ${Object.entries(ASSIGNEE_LABELS).map(([key, label]) => html`
                    <option value=${key} ?selected=${this._assigneeValue === key}>${label}</option>
                  `)}
                </select>
              </div>
            </div>

            <!-- Deadline -->
            <div class="cp-todo-form__field">
              <label class="cp-todo-form__label">Deadline</label>
              <input
                class="cp-todo-form__input"
                type="date"
                .value=${this._deadlineValue}
                @change=${this._onDeadlineChange}
              />
            </div>

            <!-- Executor (Agent or Skill) -->
            <div class="cp-todo-form__field">
              <label class="cp-todo-form__label">Executor</label>
              ${this._linkedProjectId
                ? html`<cp-skill-selector .selectedIds=${this._skillIds} .projectId=${this._linkedProjectId} @skills-changed=${this._onSkillsChanged}></cp-skill-selector>`
                : html`<cp-agent-selector .value=${this._agentId} @agent-selected=${this._onAgentSelected}></cp-agent-selector>`
              }
            </div>

            ${this._submitError ? html`
              <div class="cp-todo-form__error">${this._submitError}</div>
            ` : nothing}
          </div>

          <div class="cp-todo-form__footer">
            <div>
              ${this.isEditMode ? html`
                <button
                  class="cp-todo-form__btn cp-todo-form__btn--delete"
                  ?disabled=${this._submitting}
                  @click=${this._onDelete}
                >Loeschen</button>
              ` : nothing}
            </div>
            <div class="cp-todo-form__footer-actions">
              <button class="cp-todo-form__btn cp-todo-form__btn--cancel" @click=${this._close}>Abbrechen</button>
              <button
                class="cp-todo-form__btn cp-todo-form__btn--submit"
                ?disabled=${!canSubmit}
                @click=${this._onSubmit}
              >${submitLabel}</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

// Auto-attach modal to DOM and listen for trigger events
let formInstance: CpTodoForm | null = null;

function getForm(): CpTodoForm {
  if (!formInstance || !formInstance.isConnected) {
    formInstance = document.createElement('cp-todo-form') as CpTodoForm;
    document.body.appendChild(formInstance);
  }
  return formInstance;
}

document.addEventListener('todo-create-request', (e: Event) => {
  const detail = (e as CustomEvent).detail as { pseudoProjectId?: number } | undefined;
  const projectId = detail?.pseudoProjectId ?? 0;
  if (projectId) {
    getForm().openCreate(projectId);
  }
});

document.addEventListener('todo-edit-request', (e: Event) => {
  const detail = (e as CustomEvent).detail as { todo?: TodoWithMeta } | undefined;
  if (detail?.todo) {
    getForm().openEdit(detail.todo);
  }
});

declare global {
  interface HTMLElementTagNameMap {
    'cp-todo-form': CpTodoForm;
  }
}
